import { PLAN_TYPE_504, PLAN_TYPE_IEP, PLAN_TYPE_IPP } from '../arch/iepConstants'

const PLAN_LABEL_504 = '504 Plan'
const PLAN_LABEL_IEP = 'Individualized Education Plan (IEP)'

// This object serves as the “prototype” for values that vary based on plan type; it is defined here
// to serve as a type definitions of sorts. It can also be used as a fallback if the plan type is
// missing or unrecognized for some reason.
const PLAN_DEFAULT = {
  assistantTitle: 'Plan Assistant',
  planLabel: 'Plan',
  fullPlanLabel: 'School Document',
  plopLabel: 'Present levels of performance (PLOPs):',

  expectationsIntroduction: [
    'The Plan Assistant uses carefully tuned AI models to extract and organize key information from your child’s plan.',
    'Like any system that interprets documents, it isn’t perfect. It may occasionally miss details or interpret',
    'something differently than you would. Even so, it is an invaluable tool for understanding your child’s plan and',
    'improving how they are supported in school.'
  ].join(' '),

  expectationsSmartHelperBullet: {
    title: 'A smart helper that spots small inconsistencies',
    copy: 'or unclear wording in goals — like a Grammarly for your plan.'
  },

  accommodationsIntroduction: [
    'Accommodations describe how your child will access instruction, demonstrate learning, and',
    'participate in school. As you review them, Andy AI will highlight areas that may be unclear,',
    'incomplete, or not well aligned with your child’s needs.'
  ].join(' '),

  accommodationsKeyPoints: {
    leftItems: [
      {
        title: 'Location',
        description: [
          'Is each accommodation or modification provided in the appropriate setting,',
          'such as the general education classroom or across all settings?'
        ].join(' ')
      },

      {
        title: 'Support for success',
        description: [
          'Do these supports help your child meaningfully access the curriculum and',
          'participate alongside peers?'
        ].join(' ')
      }
    ],

    rightItems: [
      {
        title: 'Need and alignment',
        description: [
          'Does your child’s Present Levels section clearly explain why each accommodation',
          'is necessary?'
        ].join(' ')
      },

      {
        title: 'Impact',
        description: [
          'Understand how any accommodations may change grade-level expectations, credit accumulation, or',
          'diploma pathways, and whether they align with your child’s long-term goals.'
        ].join(' ')
      }
    ]
  },

  // The prelude is an array of strings because it may consist of multiple paragraphs.
  accommodationsPrelude: [
    'Accommodations should be consistent, portable, and clearly documented.',

    [
      'We check whether supports referenced in goals are formally listed, whether locations are clearly',
      'defined, and whether accommodations apply across all relevant settings. When something is missing or',
      'overly limited, we flag it so supports don’t disappear depending on the class, teacher, or situation.'
    ].join(' ')
  ],

  accommodationsPostlude: undefined,

  summary: {
    title: 'Plan Overview',
    showPlacement: true,
    showEsy: true,
    triennialDateLabel: 'Triennial date'
  },

  unlockTitle: 'You’ve seen the summary. Let’s get specific about what to ask for.',

  unlockSubtitle: [
    'The full review shows you exactly where this IEP falls short and what to push for at your next meeting.',
    'A better IEP means better days for your child, every day.'
  ].join(' '),

  unlockPrelude: [
    'Your child’s plan includes several areas that could be improved to better support their learning.',
    'Andy AI has identified opportunities to clarify these supports and ensure they fully align with',
    'your child’s documented needs.'
  ].join(' '),

  unlockButtonLabel: 'Unlock full plan review'
}

// This object contains copy and other values that vary based on plan type.
const PLAN_VARIANTS = {
  [PLAN_TYPE_IEP]: {
    assistantTitle: 'IEP Assistant',
    planLabel: 'IEP',
    fullPlanLabel: 'Individualized Education Program (IEP)',
    plopLabel: PLAN_DEFAULT.plopLabel,

    expectationsIntroduction: [
      'The IEP Assistant uses carefully tuned AI models to extract and organize key information from your child’s IEP.',
      'Like any system that interprets documents, it isn’t perfect. It may occasionally miss details or interpret',
      'something differently than you would. Even so, it is an invaluable tool for understanding your child’s IEP and',
      'improving how they are supported in school.'
    ].join(' '),

    expectationsSmartHelperBullet: {
      title: 'A smart helper that spots small inconsistencies',
      copy: 'or unclear wording in goals — like a Grammarly for your IEP.'
    },

    accommodationsIntroduction: PLAN_DEFAULT.accommodationsIntroduction,
    accommodationsKeyPoints: PLAN_DEFAULT.accommodationsKeyPoints,
    accommodationsPrelude: PLAN_DEFAULT.accommodationsPrelude,
    accommodationsPostlude: 'The focus here is making sure access supports show up everywhere they’re needed.',

    summary: {
      ...PLAN_DEFAULT.summary,

      title: 'Individualized Education Plan Overview'
    },

    unlockTitle: PLAN_DEFAULT.unlockTitle,

    unlockSubtitle: [
      'The full review shows you exactly where this IEP falls short and what to push for at your next meeting.',
      'A better IEP means better days for your child, every day.'
    ].join(' '),

    unlockPrelude: [
      'Your child’s IEP includes a goal about sharing their opinion on a story, but it doesn’t fully address',
      'their need to summarize and retell, which is a key area for their reading comprehension. Adding a goal',
      'that focuses on summarizing the main idea and key details would provide targeted support for this skill.',
      'There are also other areas in the plan, like accommodations and goal clarity, that could be improved to',
      'better support your child’s learning.'
    ].join(' '),

    unlockButtonLabel: 'Unlock full IEP review'
  },

  [PLAN_TYPE_IPP]: {
    assistantTitle: 'IPP Assistant',
    planLabel: 'IPP',
    fullPlanLabel: 'Individualized Program Plan (IPP)',
    plopLabel: PLAN_DEFAULT.plopLabel,

    expectationsIntroduction: [
      'The IPP Assistant uses carefully tuned AI models to extract and organize key information from your child’s',
      'Individual Program Plan (IPP). Like any system that interprets documents, it isn’t perfect—it may occasionally',
      'miss details or interpret something differently than you would.\n\n',
      'Even so, it’s a powerful tool to help you better understand your child’s supports, identify gaps,',
      'and feel more confident advocating their needs.'
    ].join(' '),

    expectationsSmartHelperBullet: {
      title: 'A smart helper that spots small inconsistencies',
      copy: 'or unclear wording in goals — like a Grammarly for your IPP.'
    },

    summary: {
      ...PLAN_DEFAULT.summary,

      title: 'Individualized Program Plan Overview',
      showPlacement: false,
      showEsy: false
    },

    unlockTitle: PLAN_DEFAULT.unlockTitle,

    unlockSubtitle: [
      'The full review shows you exactly where this IPP falls short and what to push for at your next meeting.',
      'A better IPP means better days for your child, every day.'
    ].join(' '),

    unlockPrelude: [
      'Your child’s IPP includes several areas that could be improved to better support their development.',
      'Andy AI has identified opportunities to clarify these services and supports and ensure they fully align',
      'with your child’s documented needs.'
    ].join(' '),

    unlockButtonLabel: 'Unlock full IPP review'
  },

  [PLAN_TYPE_504]: {
    assistantTitle: '504 Assistant',
    planLabel: '504 Plan',
    fullPlanLabel: '504 Plan',
    plopLabel: 'Assessments:',

    expectationsIntroduction: [
      'The 504 Assistant uses carefully tuned AI models to extract and organize key information from your child’s',
      '504 Plan, helping you spot gaps, vague language, and missing protections. Like any system that interprets',
      'documents, it may occasionally miss details or interpret something differently than you would—it should be used',
      'as a starting point, not a final review.'
    ].join(' '),

    expectationsSmartHelperBullet: {
      title: 'A smart helper that spots vague or unenforceable language',
      copy: 'in your accommodations — like a Grammarly for your 504 Plan.'
    },

    accommodationsIntroduction: [
      'Accommodations describe how your child will access instruction, demonstrate learning, and',
      'participate in school. As you review them, Andy AI will highlight areas that may be unclear,',
      'unenforceable, or not well aligned with your child’s needs.'
    ].join(' '),

    accommodationsKeyPoints: {
      leftItems: [
        {
          title: 'Specificity',
          description: [
            'Is each accommodation clearly defined — not just what support is provided,',
            'but when it’s triggered and how often it applies?'
          ].join(' ')
        },

        {
          title: 'Scope',
          description: [
            'Does each accommodation specify where it applies — in the general education classroom,',
            'during testing, across all settings?'
          ].join(' ')
        }
      ],

      rightItems: [
        {
          title: 'Need and alignment',
          description: [
            'Does your child’s 504 evaluation and supporting documentation clearly explain',
            'why each accommodation is necessary?'
          ].join(' ')
        },

        {
          title: 'Impact',
          description: [
            'Understand whether any listed supports go beyond access accommodations and actually change',
            'grade-level expectations — which could signal your child needs a more comprehensive evaluation.'
          ].join(' ')
        }
      ]
    },

    accommodationsPrelude: [
      [
        'Accommodations should be specific, enforceable, and consistent across settings.',
        'We review each accommodation for clarity, scope, and whether it includes',
        'a clear trigger—because vague language like “extra time as needed” is difficult to enforce.',
        'We also check whether your child’s documented areas of need are fully covered, and flag any supports',
        'that may actually be modifications requiring stronger protections. When something is missing, vague,',
        'or limited to certain settings, we flag it with specific language you can bring to your child’s 504 team.'
      ].join(' ')
    ],

    accommodationsPostlude: undefined,

    summary: {
      title: '504 Plan Overview',
      showPlacement: false,
      showEsy: false,
      triennialDateLabel: 'Re-evaluation date'
    },

    unlockTitle: PLAN_DEFAULT.unlockTitle,

    unlockSubtitle: [
      'The full review shows you exactly where this 504 Plan falls short and what to push for at your next meeting.',
      'The right accommodations mean better days for your child, every day.'
    ].join(' '),

    unlockPrelude: [
      'Your child’s 504 Plan includes several accommodations, but some may lack the specific triggers or',
      'frequency needed for consistent enforcement. Andy AI has identified opportunities to clarify these',
      'supports and ensure they fully align with your child’s documented needs, helping to prevent gaps in',
      'support across different school settings.'
    ].join(' '),

    unlockButtonLabel: 'Unlock full 504 Plan review'
  }
}
const KEY_DOCUMENTS_ICON_BACKGROUND_COLOR = 'linear-gradient(51deg, #B0FF4E 20.97%, #E2FFC0 75.54%, #F6FFE7 107.62%)'

// The standard tag IDs that indicate various plan documents.
const IEP_DOCUMENT_TAG_IDS = [[1], [44]]
const IPP_DOCUMENT_TAG_IDS = [[4], [84, 143]]
const PLAN_504_TAG_IDS = [[1], [6]]

const EXTRACTION_STATUS_COMPLETED = 'completed'

// Statuses the API can report for an extraction that is not actually running.
const EXTRACTION_STATUSES_NOT_RUNNING = ['unstarted', 'error', 'failed']

// What it takes to analyze each kind of document: the label used in copy, the plan type (for tracking), and the
// `documentType` the extraction endpoint expects (504s are extracted as IEPs).
const ANALYZABLE_DOCUMENTS = [
  { tags: IEP_DOCUMENT_TAG_IDS, label: 'IEP', planType: PLAN_TYPE_IEP, documentType: 'iep' },
  { tags: PLAN_504_TAG_IDS, label: '504', planType: PLAN_TYPE_504, documentType: 'iep' },
  { tags: IPP_DOCUMENT_TAG_IDS, label: 'IPP', planType: PLAN_TYPE_IPP, documentType: 'ipp' }
]

// The standard tag IDs of each plan type’s documents, by plan type.
const PLAN_DOCUMENT_TAG_IDS = Object.fromEntries(ANALYZABLE_DOCUMENTS.map(({ planType, tags }) => [planType, tags]))

const CANCEL_ALL_UPLOADS_WARNING = 'Are you sure you want to cancel? All documents will NOT be included in your review'
const CANCEL_ONE_UPLOAD_WARNING = 'Are you sure you want to cancel? The document will NOT be included in your review'

// The `status` a progress update carries when the back end has given up on the document. This is the update’s
// own status, not one of the pipeline statuses below, so it maps to no percentage.
const PROGRESS_STATUS_FAILED = 'failed'

// Mapping of back-end progress statuses to numeric values.
//
// Seth’s original mappings were:
// - PIPELINE_STARTED = 2%
// - CONVERTING_PDF_TO_IMAGES = 5%
// - METADATA_EXTRACTED = 12%
// - SECTION_COMPLETED = 45%
// - GOAL_SCORING_STARTED = 53%
// - GOAL_FILTERING_STARTED = 59%
// - LINK_GOALS_PLOPS_STARTED = 64%
// - BASELINE_EVAL_STARTED = 71%
// - DEDUPE_COMPLETED = 86%
//
// https://undividedapp.slack.com/archives/C03DZ44PAJJ/p1759466309002449
//
// Additional milestones were added for completeness in case a component renders mid-progress
// (because otherwise, it will be stuck at a falsy value until the next recognized milestone).
const IEP_PROGRESS_MILESTONES = {
  PIPELINE_STARTED: 2,
  CONVERTING_PDF_TO_IMAGES: 5,
  CONVERTING_IMAGES_TO_BASE64: 6,
  CLASSIFYING_SECTIONS: 7,
  SECTIONS_CLASSIFIED: 8,
  METADATA_EXTRACTING: 9,
  METADATA_EXTRACTED: 12,
  EXTRACTING_SECTIONS: 23,
  SECTION_COMPLETED: 45,
  LINK_GOALS_PLOPS_STARTED: 47,
  LINK_GOALS_PLOPS_COMPLETED: 50,
  GOAL_SCORING_STARTED: 53,
  GOAL_SCORING_COMPLETED: 55,
  GOAL_FILTERING_STARTED: 59,
  GOAL_FILTERING_COMPLETED: 60,
  SERVICES_REVIEW_STARTED: 61,
  SERVICES_REVIEW_COMPLETED: 63,
  ACCOMMODATIONS_GAP_REVIEW_STARTED: 64,
  ACCOMMODATIONS_GAP_REVIEW_COMPLETED: 66,
  BASELINE_EVAL_STARTED: 71,
  BASELINE_EVAL_COMPLETED: 72,
  MEASUREMENT_EVAL_STARTED: 73,
  MEASUREMENT_EVAL_COMPLETED: 75,
  SERVICE_SUFFICIENCY_STARTED: 80,
  SERVICE_SUFFICIENCY_COMPLETED: 83,
  DEDUPE_COMPLETED: 84,
  LINK_PLOPS_ACCOM_STARTED: 85,
  LINK_PLOPS_ACCOM_COMPLETED: 86,
  LINK_PROGRESS_TO_GOALS_STARTED: 87,
  LINK_PROGRESS_TO_GOALS_COMPLETED: 88,
  AREAS_OF_NEED_STARTED: 89,
  AREAS_OF_NEED_COMPLETED: 90,
  AREAS_OF_NEED_COVERAGE_STARTED: 91,
  AREAS_OF_NEED_COVERAGE_COMPLETED: 92,
  ED_CODE_COMPLIANCE_STARTED: 93,
  ED_CODE_COMPLIANCE_COMPLETED: 94,
  SUMMARY_STARTED: 95,
  SUMMARY_COMPLETED: 96,
  NOTIFICATIONS_STARTED: 97,
  NOTIFICATIONS_COMPLETED: 98,

  // IPP post-processing.
  IPP_GOAL_SCORING_STARTED: 46,
  IPP_GOAL_SCORING_COMPLETED: 49,
  IPP_PRIORITY_SCORING_STARTED: 50,
  IPP_PRIORITY_SCORING_COMPLETED: 52,
  IPP_OUTCOME_SCORING_STARTED: 53,
  IPP_OUTCOME_SCORING_COMPLETED: 55,
  IPP_SUPPORT_SCORING_STARTED: 56,
  IPP_SUPPORT_SCORING_COMPLETED: 58,
  IPP_AREAS_OF_NEED_STARTED: 59,
  IPP_AREAS_OF_NEED_COMPLETED: 62,
  IPP_RC_SUPPORT_STARTED: 74,
  IPP_RC_SUPPORT_COMPLETED: 78,
  IPP_SERVICE_ENHANCEMENT_STARTED: 80,
  IPP_SERVICE_ENHANCEMENT_COMPLETED: 83,
  IPP_SUMMARY_STARTED: 90,
  IPP_SUMMARY_COMPLETED: 92,

  // AON standalone extraction.
  AON_PREDICTION_STARTED: 15,
  AON_PREDICTION_BATCH_COMPLETED: 50,
  AON_PREDICTION_COMPLETED: 95,
  AON_NO_AREAS_IDENTIFIED: 95,
  AON_PREDICTION_SKIPPED: 95,

  // All roads lead to Rome 😄
  PIPELINE_COMPLETED: 99
}

// The wizard value to add to a review percentage to indicate wizard progress.
// This baseline is needed because there are some preliminary steps that do not actually advance
// the review percentage of the IEP, but should still show progress through the wizard.
// The review percentage is then actually the percentage of fraction from this baseline to 100%.
const IEP_WIZARD_REVIEW_BASELINE = 30

// Although these look like arch/constants, they are actually internal to the web app
// because they are assigned by model.js and are not part of the back-end API.
const PLAN_TYPE_GOAL = 'goal'
const PLAN_TYPE_SERVICE = 'service'
const IEP_TYPE_PLOP = 'plop'
const IEP_TYPE_ACCOMMODATION = 'accommodation'
const IEP_TYPE_MODIFICATION = 'modification'
const IPP_TYPE_PRIORITY = 'priority'
const IPP_TYPE_OUTCOME = 'outcome'
const IPP_TYPE_SUPPORT = 'support'

// A list selector for the combined services+supports listing—not an item type that model.js
// ever stamps on an item.
const IPP_TYPE_SERVICES_SUPPORTS = 'servicesSupports'

// A plan’s general recommendations grouped by category, each group standing as an item of its
// own for the general recommendations page. Synthesized on the web (see
// getPlanWithRecommendationCategories in the utilities); the API has no such collection.
const PLAN_TYPE_RECOMMENDATION_CATEGORY = 'recommendationCategory'

// A section’s summary—the extractor’s rollup of the section plus the loose recommendations placed
// in it—standing as an item of its own at the head of the section’s page. Synthesized on the web
// (see getPlanWithSectionSummaries in the utilities); the API has no such collection.
const PLAN_TYPE_SECTION_SUMMARY = 'sectionSummary'

// The maximum number of goals before requiring prioritization.
const GOAL_PRIORITIZATION_THRESHOLD = Infinity // MVP: For now, never require prioritization.

// Standard indicator that a concern is new (not yet persisted).
const NEW_CONCERN_ID_PLACEHOLDER = '__new_concern__'

// Standardized dimensions for assorted plan assistant content.
const STATUS_RUNDOWN_WIDTH = 96
const WIZARD_HEADER_HEIGHT = 10.75 // This one is fragile—needs to stay in sync with the Wizard component.
const WIZARD_PROGRESS_HEIGHT = 0.5 // Also fragile: the wizard progress height sometimes needs to be deducted.
const WIZARD_WIDTH_MAX = 108.75

// Section-key identifiers for the plan items editable via IepSectionItemEditDrawer. Goals and
// services are shared across plan types; accommodations/modifications/PLOPs are IEP/504-only and
// priorities/outcomes/supports are IPP-only.
const SECTION_KEY_GOALS = 'goals'
const SECTION_KEY_SERVICES = 'services'
const SECTION_KEY_ACCOMMODATIONS = 'accommodations'
const SECTION_KEY_MODIFICATIONS = 'modifications'
const SECTION_KEY_PLOPS = 'plops'
const SECTION_KEY_PRIORITIES = 'priorities'
const SECTION_KEY_OUTCOMES = 'outcomes'
const SECTION_KEY_SUPPORTS = 'supports'

// A pseudo-section for the recommendation partitions, tallies, and breakdown order: the general
// recommendations have no editable item type behind them (so no SECTION_ITEM_LABELS entry either),
// and their “items” are the recommendations themselves.
const SECTION_KEY_GENERAL_RECOMMENDATIONS = 'generalRecommendations'

// A print-only pseudo-section: it isn’t editable via IepSectionItemEditDrawer like the section keys
// above, since it has no corresponding editable item type, but PrintDialog’s section checklist
// presents it alongside them.
const SECTION_KEY_SUMMARY = 'summary'

// Each plan type’s sections, in its assistant’s order.
const IEP_SECTION_KEYS = [
  SECTION_KEY_GOALS,
  SECTION_KEY_SERVICES,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_PLOPS
]

const IPP_SECTION_KEYS = [
  SECTION_KEY_PRIORITIES,
  SECTION_KEY_GOALS,
  SECTION_KEY_OUTCOMES,
  SECTION_KEY_SERVICES,
  SECTION_KEY_SUPPORTS
]

const SECTION_KEYS_BY_PLAN_TYPE = {
  [PLAN_TYPE_IEP]: IEP_SECTION_KEYS,
  [PLAN_TYPE_504]: IEP_SECTION_KEYS,
  [PLAN_TYPE_IPP]: IPP_SECTION_KEYS
}

// A loose recommendation’s `section` names the section the extractor placed it in, as one of the
// section keys above (see recommendationPlacement.js). The placeable keys are the sections with a
// page of their own to show the placement: PLOPs have no page, and abilities stay with the general
// recommendations until an IPP abilities page exists. Any other value reads as general.
const PLACEMENT_GENERAL = 'general'

const PLACEABLE_SECTION_KEYS = [
  SECTION_KEY_GOALS,
  SECTION_KEY_SERVICES,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_PRIORITIES,
  SECTION_KEY_OUTCOMES,
  SECTION_KEY_SUPPORTS
]

// Singular, human-readable label for a single item within each section. Shared between the drawer
// (titles, save/delete copy) and MoreOptionsEditMenu (its "Add …" menu item) so the wording is
// always consistent regardless of which component renders it.
const SECTION_ITEM_LABELS = {
  [SECTION_KEY_GOALS]: 'Goal',
  [SECTION_KEY_SERVICES]: 'Service',
  [SECTION_KEY_ACCOMMODATIONS]: 'Accommodation',
  [SECTION_KEY_MODIFICATIONS]: 'Modification',
  [SECTION_KEY_PLOPS]: 'PLOP',
  [SECTION_KEY_PRIORITIES]: 'Priority',
  [SECTION_KEY_OUTCOMES]: 'Outcome',
  [SECTION_KEY_SUPPORTS]: 'Support'
}

export {
  ANALYZABLE_DOCUMENTS,
  CANCEL_ALL_UPLOADS_WARNING,
  CANCEL_ONE_UPLOAD_WARNING,
  EXTRACTION_STATUS_COMPLETED,
  EXTRACTION_STATUSES_NOT_RUNNING,
  GOAL_PRIORITIZATION_THRESHOLD,
  IEP_DOCUMENT_TAG_IDS,
  IEP_PROGRESS_MILESTONES,
  IEP_TYPE_ACCOMMODATION,
  IEP_TYPE_MODIFICATION,
  IEP_TYPE_PLOP,
  IEP_WIZARD_REVIEW_BASELINE,
  IPP_DOCUMENT_TAG_IDS,
  IPP_TYPE_OUTCOME,
  IPP_TYPE_PRIORITY,
  IPP_TYPE_SERVICES_SUPPORTS,
  IPP_TYPE_SUPPORT,
  KEY_DOCUMENTS_ICON_BACKGROUND_COLOR,
  NEW_CONCERN_ID_PLACEHOLDER,
  PROGRESS_STATUS_FAILED,
  SECTION_KEY_GOALS,
  SECTION_KEY_SERVICES,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_PLOPS,
  SECTION_KEY_PRIORITIES,
  SECTION_KEY_OUTCOMES,
  SECTION_KEY_SUPPORTS,
  SECTION_KEY_GENERAL_RECOMMENDATIONS,
  SECTION_KEY_SUMMARY,
  SECTION_KEYS_BY_PLAN_TYPE,
  PLACEMENT_GENERAL,
  PLACEABLE_SECTION_KEYS,
  SECTION_ITEM_LABELS,
  PLAN_504_TAG_IDS,
  PLAN_DOCUMENT_TAG_IDS,
  PLAN_DEFAULT,
  PLAN_LABEL_504,
  PLAN_LABEL_IEP,
  PLAN_TYPE_GOAL,
  PLAN_TYPE_RECOMMENDATION_CATEGORY,
  PLAN_TYPE_SECTION_SUMMARY,
  PLAN_TYPE_SERVICE,
  PLAN_VARIANTS,
  STATUS_RUNDOWN_WIDTH,
  WIZARD_HEADER_HEIGHT,
  WIZARD_PROGRESS_HEIGHT,
  WIZARD_WIDTH_MAX
}
